import { Media, Message } from "@twilio/conversations";
import log from "loglevel";
import { Box } from "@twilio-paste/core/box";
import { ScreenReaderOnly } from "@twilio-paste/core/screen-reader-only";
import { useDispatch, useSelector } from "react-redux";
import { Text } from "@twilio-paste/core/text";
import { Flex } from "@twilio-paste/core/flex";
import { UserIcon } from "@twilio-paste/icons/esm/UserIcon";
import { Key, KeyboardEvent, useEffect, useRef, useState } from "react";
import { SuccessIcon } from "@twilio-paste/icons/esm/SuccessIcon";

import { AppState } from "../store/definitions";
import { FilePreview } from "./FilePreview";
import { updateAwaitingBot } from "../store/actions/genericActions";
import { parseMessageBody } from "../utils/parseMessageBody";
import {
    getAvatarContainerStyles,
    getInnerContainerStyles,
    authorStyles,
    timeStampStyles,
    bodyStyles,
    outerContainerStyles,
    readStatusStyles,
    bubbleAndAvatarContainerStyles,
    messageButtonsContainerStyles,
    messageButtonStyles,
    messageButtonInlineStyle,
    BUBBLE_LEFT_INSET_PX
} from "./styles/MessageBubble.styles";

// Conteúdo rico vindo do backend nos attributes da mensagem (modelo Zendesk Ultimate AI)
interface MessageButton {
    label: string;
    value: string;
}
interface MessageRichAttributes {
    sender?: { name?: string; avatar?: string };
    buttons?: MessageButton[];
}

const doubleDigit = (number: number) => `${number < 10 ? 0 : ""}${number}`;

export const MessageBubble = ({
    message,
    isLast,
    isLastOfUserGroup,
    focusable,
    updateFocus
}: {
    message: Message;
    isLast: boolean;
    isLastOfUserGroup: boolean;
    focusable: boolean;
    updateFocus: (newFocus: number) => void;
}) => {
    const [read, setRead] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [buttonsUsed, setButtonsUsed] = useState(false);
    const dispatch = useDispatch();
    const { conversationsClient, conversation, participants, users, fileAttachmentConfig, awaitingBot } = useSelector(
        (state: AppState) => ({
            conversationsClient: state.chat.conversationsClient,
            conversation: state.chat.conversation,
            participants: state.chat.participants,
            users: state.chat.users,
            fileAttachmentConfig: state.config.fileAttachment,
            awaitingBot: state.chat.awaitingBot
        })
    );
    const messageRef = useRef<HTMLDivElement>(null);

    const belongsToCurrentUser = message.author === conversationsClient?.user.identity;

    // Conteúdo rico (nome/foto do bot e botões) — o front só desenha o que o backend manda
    const richAttributes = (message.attributes ?? {}) as MessageRichAttributes;
    const { sender } = richAttributes;
    const buttons = Array.isArray(richAttributes.buttons) ? richAttributes.buttons : [];

    const handleButtonClick = (button: MessageButton) => {
        if (!conversation || awaitingBot) {
            return;
        }
        setButtonsUsed(true); // some com os botões após a escolha (evita clique duplo)
        dispatch(updateAwaitingBot(true)); // entra em "esperando o bot" na hora (lock + balão)
        conversation
            .prepareMessage()
            .setBody(button.label)
            .setAttributes({ buttonValue: button.value })
            .build()
            .send()
            .catch((e) => {
                log.error("Failed sending button choice:", e);
                setButtonsUsed(false); // reabilita se o envio falhar
            });
    };

    useEffect(() => {
        if (isLast && participants && belongsToCurrentUser) {
            const getOtherParticipants = participants.filter((p) => p.identity !== conversationsClient?.user.identity);
            setRead(
                Boolean(getOtherParticipants.length) &&
                    getOtherParticipants.every((p) => p.lastReadMessageIndex === message.index)
            );
        } else {
            setRead(false);
        }
    }, [participants, isLast, belongsToCurrentUser, conversationsClient, message]);

    useEffect(() => {
        if (focusable) {
            messageRef.current?.focus();
        }
    }, [focusable]);

    const renderMedia = () => {
        if (fileAttachmentConfig?.enabled) {
            if (!message.attachedMedia) {
                return null;
            }

            return message.attachedMedia.map((media: Media, index: Key) => {
                const file = {
                    name: media.filename,
                    type: media.contentType,
                    size: media.size
                } as File;
                return <FilePreview key={index} file={file} isBubble={true} media={media} focusable={focusable} />;
            });
        }

        return <i>Mensagens de mídia não são suportadas</i>;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            const newFocusValue = message.index + (e.key === "ArrowUp" ? -1 : 1);
            updateFocus(newFocusValue);
        }
    };

    const handleMouseDown = () => {
        setIsMouseDown(true);
    };

    const handleMouseUp = () => {
        setIsMouseDown(false);
    };

    const handleFocus = () => {
        // Ignore focus from clicks
        if (!isMouseDown) {
            // Necessary since screen readers can set the focus to any focusable element
            updateFocus(message.index);
        }
    };

    const author = sender?.name || users?.find((u) => u.identity === message.author)?.friendlyName || message.author;

    return (
        <Box
            {...outerContainerStyles}
            tabIndex={focusable ? 0 : -1}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            ref={messageRef}
            data-message-bubble
            data-testid="message-bubble"
        >
            <Box {...bubbleAndAvatarContainerStyles}>
                {!belongsToCurrentUser && (
                    <Box {...getAvatarContainerStyles(!isLastOfUserGroup)} data-testid="avatar-container">
                        {isLastOfUserGroup &&
                            (sender?.avatar ? (
                                <img
                                    src={sender.avatar}
                                    alt={author}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            ) : (
                                <UserIcon decorative={true} size="sizeIcon40" />
                            ))}
                    </Box>
                )}
                <Box
                    {...getInnerContainerStyles(belongsToCurrentUser)}
                    style={belongsToCurrentUser ? { backgroundColor: "#00363d" } : undefined}
                >
                    <Flex hAlignContent="between" width="100%" vAlignContent="center" marginBottom="space20">
                        <Text {...authorStyles} as="p" aria-hidden style={{ textOverflow: "ellipsis" }} title={author}>
                            {author}
                        </Text>
                        <ScreenReaderOnly as="p">
                            {belongsToCurrentUser
                                ? "Você enviou às"
                                : `${users?.find((u) => u.identity === message.author)?.friendlyName} enviou às`}
                        </ScreenReaderOnly>
                        <Text {...timeStampStyles} as="p">
                            {`${doubleDigit(message.dateCreated.getHours())}:${doubleDigit(
                                message.dateCreated.getMinutes()
                            )}`}
                        </Text>
                    </Flex>
                    <Text as="p" {...bodyStyles}>
                        {message.body ? parseMessageBody(message.body, belongsToCurrentUser) : null}
                    </Text>
                    {message.type === "media" ? renderMedia() : null}
                </Box>
            </Box>
            {/* Quick-replies só valem para a pergunta ATUAL: aparecem apenas na última mensagem
                (do bot). Qualquer mensagem nova — do cliente (digitada/clique) ou novo turno do
                bot — faz esta bolha deixar de ser a última e os botões somem. Histórico nunca é clicável. */}
            {isLast && !belongsToCurrentUser && buttons.length > 0 && !buttonsUsed && (
                <Box
                    {...messageButtonsContainerStyles}
                    style={{ gap: "8px", marginLeft: `${BUBBLE_LEFT_INSET_PX}px` }}
                    data-test="message-buttons"
                >
                    {buttons.map((button, index) => (
                        <Box
                            as="button"
                            type="button"
                            key={index}
                            data-test="message-button"
                            onClick={() => handleButtonClick(button)}
                            aria-disabled={awaitingBot}
                            {...messageButtonStyles}
                            style={{
                                ...messageButtonInlineStyle,
                                ...(awaitingBot
                                    ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" }
                                    : {})
                            }}
                        >
                            {button.label}
                        </Box>
                    ))}
                </Box>
            )}
            {read && (
                <Flex hAlignContent="right" vAlignContent="center" marginTop="space20">
                    <Text as="p" {...readStatusStyles}>
                        Lida
                    </Text>
                    <SuccessIcon decorative={true} size="sizeIcon10" color="colorTextWeak" />
                </Flex>
            )}
        </Box>
    );
};
